import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  NarrativeWorkflowState,
  NarrativeScene,
  GenerationJob,
  StyleConfiguration,
  GeneratedImage,
  GeneratedVideo,
  WorkflowError
} from '../models/narrative.model';
import { NarrativeHelper } from '../utils/narrative-helper';
import { NARRATIVE_CONSTANTS } from '../constants/narrative.constants';

@Injectable({
  providedIn: 'root'
})
export class NarrativeStateService {
  private readonly initialState: NarrativeWorkflowState = {
    scenes: [],
    styleConfig: {
      type: 'image',
      strength: NARRATIVE_CONSTANTS.STYLE.DEFAULT_STRENGTH,
      visualIntensity: NARRATIVE_CONSTANTS.IMAGE_GENERATION.DEFAULT_VISUAL_INTENSITY,
      videoSettings: {
        selectedSize: '540p'
      }
    },
    isGeneratingImages: false,
    isGeneratingVideos: false,
    isCombiningVideos: false,
    imageGenerationProgress: 0,
    videoGenerationProgress: 0,
    activeJobs: new Map(),
    errors: []
  };

  private state$ = new BehaviorSubject<NarrativeWorkflowState>(this.initialState);

  // Observables for components to subscribe to
  readonly workflowState$ = this.state$.asObservable();

  // Computed states
  get currentState(): NarrativeWorkflowState {
    return this.state$.value;
  }

  get scenes$(): Observable<NarrativeScene[]> {
    return new Observable(observer => {
      const subscription = this.state$.subscribe(state => {
        observer.next(state.scenes);
      });
      return () => subscription.unsubscribe();
    });
  }

  get isProcessing$(): Observable<boolean> {
    return new Observable(observer => {
      const subscription = this.state$.subscribe(state => {
        observer.next(
          state.isGeneratingImages ||
          state.isGeneratingVideos ||
          state.isCombiningVideos
        );
      });
      return () => subscription.unsubscribe();
    });
  }

  /**
   * シーンを初期化
   */
  initializeScenes(scenes: Array<Partial<NarrativeScene>>): void {
    const newScenes = scenes.map(scene => this.createScene(scene));
    this.updateState({ scenes: newScenes });
  }

  /**
   * シーンを追加
   */
  addScene(scene: Partial<NarrativeScene>): void {
    const newScene = this.createScene(scene);
    const scenes = [...this.currentState.scenes, newScene];
    this.updateState({ scenes });
  }

  /**
   * シーンを更新
   */
  updateScene(sceneId: string, updates: Partial<NarrativeScene>): void {
    const scenes = this.currentState.scenes.map(scene =>
      scene.id === sceneId ? { ...scene, ...updates } : scene
    );
    this.updateState({ scenes });
  }

  /**
   * シーンを削除
   */
  removeScene(sceneId: string): void {
    const scenes = this.currentState.scenes.filter(scene => scene.id !== sceneId);
    this.updateState({ scenes });
  }

  /**
   * スタイル設定を更新
   */
  updateStyleConfig(config: Partial<StyleConfiguration>): void {
    const styleConfig = { ...this.currentState.styleConfig, ...config };
    this.updateState({ styleConfig });
  }

  /**
   * 画像生成の状態を更新
   */
  setImageGenerationState(isGenerating: boolean, progress: number = 0): void {
    this.updateState({
      isGeneratingImages: isGenerating,
      imageGenerationProgress: progress
    });
  }

  /**
   * 動画生成の状態を更新
   */
  setVideoGenerationState(isGenerating: boolean, progress: number = 0): void {
    this.updateState({
      isGeneratingVideos: isGenerating,
      videoGenerationProgress: progress
    });
  }

  /**
   * 動画結合の状態を更新
   */
  setVideoCombinationState(isCombining: boolean): void {
    this.updateState({ isCombiningVideos: isCombining });
  }

  /**
   * 結合された動画のURLを設定
   */
  setCombinedVideoUrl(url: string | undefined): void {
    this.updateState({ combinedVideoUrl: url });
  }

  /**
   * シーンに画像を追加
   */
  addImagesToScene(sceneId: string, images: GeneratedImage[]): void {
    const scenes = this.currentState.scenes.map(scene =>
      scene.id === sceneId
        ? { ...scene, images, status: 'completed' as const }
        : scene
    );
    this.updateState({ scenes });
  }

  /**
   * シーンの選択された画像インデックスを更新
   */
  updateSelectedImageIndex(sceneId: string, index: number): void {
    const scenes = this.currentState.scenes.map(scene =>
      scene.id === sceneId
        ? { ...scene, selectedImageIndex: index }
        : scene
    );
    this.updateState({ scenes });
  }

  /**
   * シーンに動画を設定
   */
  setSceneVideo(sceneId: string, video: GeneratedVideo): void {
    const scenes = this.currentState.scenes.map(scene =>
      scene.id === sceneId
        ? { ...scene, video, status: 'completed' as const }
        : scene
    );
    this.updateState({ scenes });
  }

  /**
   * ジョブを追加
   */
  addJob(job: GenerationJob): void {
    const activeJobs = new Map(this.currentState.activeJobs);
    activeJobs.set(job.id, job);
    this.updateState({ activeJobs });
  }

  /**
   * ジョブを更新
   */
  updateJob(jobId: string, updates: Partial<GenerationJob>): void {
    const activeJobs = new Map(this.currentState.activeJobs);
    const job = activeJobs.get(jobId);
    if (job) {
      activeJobs.set(jobId, { ...job, ...updates });
      this.updateState({ activeJobs });
    }
  }

  /**
   * ジョブを削除
   */
  removeJob(jobId: string): void {
    const activeJobs = new Map(this.currentState.activeJobs);
    activeJobs.delete(jobId);
    this.updateState({ activeJobs });
  }

  /**
   * エラーを追加
   */
  addError(error: WorkflowError): void {
    const errors = [...this.currentState.errors, error];
    this.updateState({ errors });
  }

  /**
   * エラーをクリア
   */
  clearErrors(): void {
    this.updateState({ errors: [] });
  }

  /**
   * 状態をリセット
   */
  resetState(): void {
    this.state$.next(this.initialState);
  }

  /**
   * シーンのステータスを更新
   */
  updateSceneStatus(sceneId: string, status: NarrativeScene['status']): void {
    const scenes = this.currentState.scenes.map(scene =>
      scene.id === sceneId ? { ...scene, status } : scene
    );
    this.updateState({ scenes });
  }

  /**
   * すべてのシーンが画像を持っているかチェック
   */
  areAllScenesGenerated(): boolean {
    return this.currentState.scenes.length > 0 &&
      this.currentState.scenes.every(scene => scene.images.length > 0);
  }

  /**
   * 複数のシーン動画があるかチェック
   */
  hasMultipleSceneVideos(): boolean {
    const videosCount = this.currentState.scenes.filter(scene => scene.video).length;
    return videosCount >= 2;
  }

  /**
   * 新しいシーンを作成
   */
  private createScene(partial: Partial<NarrativeScene>): NarrativeScene {
    return {
      id: NarrativeHelper.generateId(),
      prompt: '',
      images: [],
      selectedImageIndex: 0,
      status: 'idle',
      ...partial
    };
  }

  /**
   * 状態を更新
   */
  private updateState(updates: Partial<NarrativeWorkflowState>): void {
    this.state$.next({ ...this.currentState, ...updates });
  }
}